import React, { useState, useRef, useEffect } from "react";
import "../styles/addOffer.css"; // The path to your CSS file
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setShowMessage } from "../redux/slices/notificationSlice";
import axios from "axios";
import CommonServices from "../services/CommonServices";
import { useSelector } from "react-redux";
import translations from "../assets/locals/translations";

const AddOffer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const commonService = new CommonServices();

  const editData = location.state?.data;
  const isEditMode = !!editData;

  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations["en"];

  // --- STATE MANAGEMENT ---
  const [offerName, setOfferName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [offerContent, setOfferContent] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [existingAttachmentUrl, setExistingAttachmentUrl] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // --- DATA POPULATION FOR EDIT MODE ---
  useEffect(() => {
    // If we have edit data from the location state, populate the form
    if (editData) {
      console.log(
        "Edit Mode: Populating form with data passed from parent:",
        editData
      );
      setOfferName(editData.name || "");
      setStartDate(editData.startDate || "");
      setEndDate(editData.endDate || "");
      setOfferContent(editData.content || "");
      setExistingAttachmentUrl(editData.attachmentUrl || null);
    }
  }, [editData]); // This effect runs when the component mounts with editData

  // --- HANDLERS ---
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setExistingAttachmentUrl(null); // Clear the old file reference if a new one is chosen
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setExistingAttachmentUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatOfferData = (content, file) => {
    const offerArray = [];

    // Add the textual content as a string
    if (content && typeof content === "string") {
      offerArray.push({
        contentType: "string",
        content: content.trim(),
      });
    }

    // Add the file as MessageMedia if present
    if (file) {
      offerArray.push({
        contentType: "MessageMedia",
        content: file,
      });
    }

    return offerArray;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validation
    if (
      !offerName.trim() ||
      !startDate ||
      !endDate ||
      !offerContent.trim() ||
      (!isEditMode && !selectedFile)
    ) {
      dispatch(setShowMessage(t.please_fill_required_fields || "Please fill out all required fields."));
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
      dispatch(setShowMessage(t.enddate_before_startdate || "End Date cannot be before Start Date."));
      return;
    }

    // Prepare FormData
    const messagesStackData = formatOfferData(offerContent, selectedFile);
    const formData = new FormData();
    formData.append("offername", offerName);
    formData.append("startDate", startDate);
    formData.append("endDate", endDate);
    formData.append("sendMessages", JSON.stringify(messagesStackData));
    if (selectedFile) {
      formData.append("files", selectedFile);
    }

    setIsSubmitting(true);
    try {
      let response;
      if (isEditMode && editData?.id) {
        formData.append("offerId", editData.id);
        response = await commonService.UpdateOffer(formData);
      } else {
        response = await commonService.AddOffer(formData);
      }

      if (response.isSuccess) {
        dispatch(setShowMessage(response.message));
        navigate("/administration/configuration/offer");
      }
    } catch (error) {
      console.error("Error saving offer:", error);
      dispatch(setShowMessage(t.error_saving_offer || "An error occurred while saving the offer."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="clean-add-offer-scope">
      <div className="form-card">
        <header className="form-header">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="text-secondary mb-0">
              {isEditMode ? (t.editoffer || "Edit Offer") : (t.addnewoffer || "Add New Offer")}
            </h4>
            <button
              className="btn btn-outline-secondary d-flex align-items-center ms-1"
              onClick={() => navigate("/administration/configuration/offer")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="currentColor"
                className="bi bi-arrow-left-circle me-2"
                viewBox="0 0 16 16"
              >
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                <path d="M8.354 11.354a.5.5 0 0 1-.708 0L4.5 8.207l3.146-3.147a.5.5 0 0 1 .708.708L5.707 8l2.647 2.646a.5.5 0 0 1 0 .708z" />
              </svg>
              {t.back || 'Back'}
            </button>
          </div>
          <p>
            {isEditMode
              ? (t.updateoffer_description || "Update the details for this promotional offer.")
              : (t.addoffer_description || "Provide the details for the new promotional offer.")}
          </p>
        </header>

        <form onSubmit={handleSubmit} className="form-body">
          <div className="form-group">
            <label htmlFor="offerName">{t.offername || 'Offer Name'}</label>
            <input
              type="text"
              id="offerName"
              className="input-field"
              placeholder={t.offername_placeholder || 'e.g., Sonalika Sale 2025'}
              value={offerName}
              onChange={(e) => setOfferName(e.target.value)}
              required
            />
          </div>

          <div className="date-row">
            <div className="form-group">
              <label htmlFor="startDate">{t.startdate || 'Start Date'}</label>
              <input
                type="date"
                id="startDate"
                className="input-field"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="endDate">{t.enddate || 'End Date'}</label>
              <input
                type="date"
                id="endDate"
                className="input-field"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="offerContent">{t.offercontent || 'Offer Content'}</label>
            <textarea
              id="offerContent"
              rows={6}
              className="input-field"
              placeholder={t.offercontent_placeholder || "e.g., 'Sonalika Dhamaka Offers...'"}
              value={offerContent}
              onChange={(e) => setOfferContent(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="file-upload">
              {t.attachment || 'Attachment'} {isEditMode && `(${t.attachment_optional || 'Optional: to replace existing'})`}
            </label>
            {selectedFile ? (
              <div className="file-preview-container">
                <div className="file-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                    />
                  </svg>
                </div>
                <div className="file-details">
                  <span className="file-name">{selectedFile.name}</span>
                  <span className="file-size">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="remove-btn"
                  aria-label="Remove file"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ) : existingAttachmentUrl ? (
              <div className="file-preview-container">
                <div className="file-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                    />
                  </svg>
                </div>
                <div className="file-details">
                  <span className="file-name">{existingAttachmentUrl}</span>
                  <span className="file-size">{t.existingfile || 'Existing File'}</span>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="remove-btn"
                  aria-label="Remove file"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <label htmlFor="file-upload" className="file-drop-zone">
                <div className="file-drop-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 16.5V9.75m0 0l-3.75 3.75M12 9.75l3.75 3.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p>
                  <span className="upload-link">{t.clicktoupload || 'Click to upload'}</span> {t.or_dragn_drop || 'or drag and drop'}
                </p>
                <span className="upload-specs">
                  {t.upload_specs || 'PNG, JPG, or PDF (MAX. 10MB)'}
                </span>
              </label>
            )}
            <input
              id="file-upload"
              type="file"
              className="file-input-hidden"
              onChange={handleFileChange}
              ref={fileInputRef}
              accept="image/*,.pdf"
            />
          </div>

          <div className="form-actions">
            <button
              type="submit"
              disabled={isSubmitting}
              className="submit-btn"
            >
              {isSubmitting
                ? isEditMode
                  ? (t.updating || "Updating...")
                  : (t.saving || "Saving...")
                : isEditMode
                  ? (t.updateoffer || "Update Offer")
                  : (t.saveoffer || "Save Offer")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddOffer;
